import { PromotionStrip } from "@/components/MarketingPage/PromotionStrip";
import { PromotionStripUS } from "@/components/MarketingPage/PromotionStripUS";
import { ButtonSizeEnum, ButtonTypeEnum } from "@/components/atoms/Button";
import CtaButtonGroup, {
  CtaActionType,
  CtaType,
  ScheduleADemoSourceEnum,
} from "@/components/molecules/CtaButtonGroup";
import { SignInBtn } from "@/components/molecules/SignInBtn";
import { SignUpBtn } from "@/components/molecules/SignUpButton";
import { LANDING_PAGE_EVENTS } from "@/constants/amplitude.constants";
import { useI18nContext } from "@/hooks/useI18nContext";
import { UCE_CALENDLY_Affliate, UCE_CALENDLY_LINK } from "@/shared/constants/calendly.constants";
import { DEFAULT_COUNTRY_CODE } from "@/shared/constants/common.constants";
import { NavbarDataType } from "@/shared/data/navbar.data";
import Amplitude from "@/shared/services/amplitude.service";
import { useDataStore } from "@/shared/store/useDataStore";
import useModalStore, { AuthModalTypeEnum } from "@/shared/store/useModalStore";
import { useNavStore } from "@/shared/store/useNavStore";
import {
  getCurrentURL,
  getReferrer,
  getScreenWidth,
} from "@/shared/utils/commons";
import { getFromLocal } from "@/shared/utils/storage.util";
import { getPathName } from "@/shared/utils/url.util";
import { getUTMFieldsFromLocalStorage } from "@/shared/utils/utm.util";
import classNames from "classnames";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import { parseCookies } from "nookies";
import { useEffect, useState } from "react";
import { NavbarMenu } from "./NavbarMenu";

export const ROOT_HOST_NEW = `${process.env.NEXT_PUBLIC_ROOT_HOST}` || "https://graphy.com";
const SignInModal = dynamic(() => import("@/components/molecules/SignInModal"));
const NavbarMweb = dynamic(() => import("./NavbarMweb"));

export enum NavItemType {
  Menu = "Menu",
  Link = "Link",
}

export enum NavMenuItemType {
  Products = "Products",
  Features = "Features",
}

export type NavbarItemType =
  | {
      name: string;
      type: NavItemType.Menu;
      menuType: NavMenuItemType;
      menuItems: {
        name: string;
        link?: string;
      }[];
    }
  | {
      name: string;
      type: NavItemType.Link;
      link?: string;
    };

export const Navbar = ({
  navbarData,
  showBookDemoBtn,
  hideNavCta,
}: {
  navbarData: NavbarDataType;
  showBookDemoBtn?: boolean;
  hideNavCta?: boolean;
}) => {
  const {
    navbarItems = [],
    signupCopies,
    graphyLogoOnClick,
    promotionStripData,
    promotionStripDataUS,
    registrationButtonUS,
    registrationButton
  } = navbarData;

  const router = useRouter();
  const { query } = router;
  const hasWebinarSuccess = 'webinar_success' in query;
  const DISABLED_LOGO_PATHS = [
    "best-lms",
    "graphy-alternative",
    "online-tutoring-platform",
    "online-teaching-platform"
  ];  

  const { isAuthModalOpen, openAuthModal, authModalType, setOpenSignUp } =
    useModalStore();
  // console.log(navbarData);
  const { showSignUp } = useNavStore();
  const cookies = parseCookies();

  const { countryCode } = useDataStore();
  const cookieCountryCode = cookies?.countryCode || DEFAULT_COUNTRY_CODE;
  const finalCountryCode = countryCode || cookieCountryCode;

  const handleToggleAmplitude = (label: string) => {
    const { current_url, utm_params, item_name, screen_width, referrer } =
      LANDING_PAGE_EVENTS.NAV_ITEM_EXPANDED.PROPERTIES;
    if (label === "Features") {
      new Amplitude().sendEvent({
        type: LANDING_PAGE_EVENTS.NAV_ITEM_EXPANDED.LABEL,
        properties: {
          [current_url]: getCurrentURL(),
          [utm_params]: getUTMFieldsFromLocalStorage(),
          [screen_width]: getScreenWidth(),
          [referrer]: getReferrer(),
          [item_name]: "features",
        } as { [key: string]: string },
      });
    } else if (label === "Products") {
      new Amplitude().sendEvent({
        type: LANDING_PAGE_EVENTS.NAV_ITEM_EXPANDED.LABEL,
        properties: {
          [current_url]: getCurrentURL(),
          [utm_params]: getUTMFieldsFromLocalStorage(),
          [screen_width]: getScreenWidth(),
          [referrer]: getReferrer(),
          [item_name]: "products",
        } as { [key: string]: string },
      });
    }
  };
  const path = usePathname();

 

  const isAppSumoSignUp = getPathName(path) === "appsumo/signup/";
  // Marketing/CMS page detection moved off URL prefixes — products/features/
  // landing pages now share the root namespace, so we rely on the slug the
  // [slug].tsx dispatcher writes into the data store when the CMS branch wins.
  const marketingPageSlug = useDataStore((s) => s.marketingPageSlug);
  const isUsLocalePage = useI18nContext().locale === "us" && !!marketingPageSlug;
  const isUSLocalAffiliatePage =
    useI18nContext().locale === "us" && marketingPageSlug.startsWith("affiliate");
  const isWebinarPath = path?.includes("/webinar");
  const isGetStartedFbPath = path?.includes("/get-started-fbform");
  const url = router.query.page;
  const isOnlineCoursePlatform = url === "online-course-platform";
  const isWebinarRebeccaSchocker = url === "webinar-rebecca-schocker";
  const signUpPage = !path.includes('/signup/')
  
 

  const openSignInModal = () => {
    const { current_url, screen_width, referrer, source, utm_params, index } =
      LANDING_PAGE_EVENTS.SIGN_IN_TAPPED.PROPERTIES;
    const utmParams = getFromLocal("utmFields");

    new Amplitude().sendEvent({
      type: LANDING_PAGE_EVENTS.SIGN_IN_TAPPED.LABEL,
      properties: {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [referrer]: getReferrer(),
        [source]: "navbar - web",
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [index]: 0,
      } as { [key: string]: string },
    });
    if (getPathName(path) === "signup/") {
      openAuthModal(AuthModalTypeEnum.SignIn, true);
    } else {
      openAuthModal(AuthModalTypeEnum.SignIn, false);
    }
  };
  

  const openSignUpModal = () => {
    const { current_url, screen_width, referrer, source, utm_params, index } =
      LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.PROPERTIES;
    new Amplitude().sendEvent({
      type: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
      properties: {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [referrer]: getReferrer(),
        [source]: "navbar - web",
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [index]: 0,
      } as { [key: string]: string },
    });
    setOpenSignUp(true);
    if (getPathName(path) === "signup/") {
      openAuthModal(AuthModalTypeEnum.SignUp, true);
    } else {
      openAuthModal(AuthModalTypeEnum.SignUp, false);
    }
  };
  

  const renderCtaButton = () => {
    // if (isAppSumoSignUp) return null;
    if (isOnlineCoursePlatform) {
      return <CtaButtonGroup
        ctas={[
          {
            type: ButtonTypeEnum.Primary,
            size: ButtonSizeEnum.Regular,
            text: "Schedule a demo",
            onClick: () => {
            const scrollY = window.scrollY;
              if (scrollY === 0) {
                window.scrollTo({ top: 80, behavior: 'smooth' });
              } else if (scrollY > 80) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            },
            amplitudeEvent: {
              eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
              eventProperties: {
                source: ScheduleADemoSourceEnum.navbar,
              },
            },
          },
        ]}
      />;
    }
    else if (isUsLocalePage && !isWebinarPath) {
      return (
        <CtaButtonGroup
        ctas={[
          {
            type: ButtonTypeEnum.Primary,
            size: ButtonSizeEnum.Regular,
            text: isUSLocalAffiliatePage ? "Talk to an Affiliate Expert" : "Schedule a demo",
            href: isUSLocalAffiliatePage ? `${UCE_CALENDLY_Affliate}` : `${UCE_CALENDLY_LINK}?page_source=hero`,
            actionType: CtaActionType.DelayedHrefBlank,
            amplitudeEvent: {
              eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
              eventProperties: {
                source: ScheduleADemoSourceEnum.navbar,
              },
            },
          },
        ]}
      />
    );
  }

    if (isWebinarPath) {
      if (hasWebinarSuccess) {
        return null;
      } else {
        const cta: CtaType = {
          type: ButtonTypeEnum.Primary,
          size: ButtonSizeEnum.Regular,
          text: isUsLocalePage
            ? registrationButtonUS?.ctaText ?? "Register now"
            : registrationButton?.ctaText ?? "Register now",
        };

        if (isOnlineCoursePlatform || isWebinarRebeccaSchocker) {
          cta.onClick = () => {
            const scrollY = window.scrollY;
            if (scrollY === 0) {
              window.scrollTo({ top: 80, behavior: 'smooth' });
            } else if (scrollY > 80) {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
          };
        } else {
          cta.href = isUsLocalePage
            ? registrationButtonUS?.ctaLink ?? ""
            : registrationButton?.ctaLink ?? "";
          cta.actionType = CtaActionType.DelayedHref;
          cta.amplitudeEvent = {
            eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
            eventProperties: {
              source: ScheduleADemoSourceEnum.navbar,
            },
          };
        }

        return (
          <CtaButtonGroup
            ctas={[cta]}
          />
        );
      }
    }

    if (showBookDemoBtn) {
      return (
        <>
          {!hideNavCta && (
            <CtaButtonGroup
              ctas={[
                {
                  type: ButtonTypeEnum.Secondary,
                  size: ButtonSizeEnum.Regular,
                  text: signupCopies.ctas.BOOK_A_DEMO,
                  actionType: CtaActionType.BookDemo,
                },
                {
                  type: ButtonTypeEnum.Primary,
                  size: ButtonSizeEnum.Regular,
                  text: signupCopies.ctas.GET_STARTED,
                  actionType: CtaActionType.SignUp,
                },
              ]}
            />
          )}
        </>
      );
    } else {
      return getPathName(path) === "signup/" ? (
        <SignInBtn text={signupCopies.ctas.SIGN_IN} onClick={openSignInModal} />
      ) : (
        <>
          {showSignUp ? (
            <SignInBtn
              text={signupCopies.ctas.SIGN_IN}
              onClick={openSignInModal}
            />
          ) : (
            <SignUpBtn
              text={signupCopies.ctas.SIGN_UP}
              onClick={openSignUpModal}
            />
          )}
        </>
      );
    }
  };

  const actionAmplitudeEvents = (itemName: string) => {
    const { current_url, screen_width, referrer, item_name, utm_params } =
      LANDING_PAGE_EVENTS.NAV_ITEM_TAPPED.PROPERTIES;
    new Amplitude().sendEvent({
      type: LANDING_PAGE_EVENTS.NAV_ITEM_TAPPED.LABEL,
      properties: {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [referrer]: getReferrer(),
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [item_name]: itemName,
      } as { [key: string]: string },
    });
  };
  const [showStrip, setShowStrip] = useState(false);

  useEffect(() => {
    window.addEventListener("scroll", () => {
      if (scrollY < 1) {
        setShowStrip(false);
      } else {
        setShowStrip(true);
      }
    });
    return () => {
      setShowStrip(false);
    };
  }, []);
  

  return (
    <>
      <nav className="bg-navbar sticky top-0 z-bar hidden w-full  bg-white30 opacity-100 backdrop-blur-md md:block">
        <div
          className={classNames(
            "flex items-center justify-between py-6",
            isUsLocalePage
              ? "mx-auto max-w-[1440px] px-4 lg:px-[88px]"
              : "page-max-width px-4 lg:px-0"
          )}
        >
          {isGetStartedFbPath ? (
            <Image
              src="/images/graphy_logo_black.svg"
              width={100}
              height={32}
              alt="graphy"
              onClick={() => {
                actionAmplitudeEvents("logo");
              }}
            />
          ) : (
              <Link
                href={graphyLogoOnClick ? "" : ROOT_HOST_NEW}
                className={classNames("h-[32px] w-[100px]", {
                  'pointer-events-none': DISABLED_LOGO_PATHS.includes(url as string)
                })}
                onClick={(e) => {
                  // Check if current URL is in disabled paths
                  if (DISABLED_LOGO_PATHS.includes(url as string)) {
                    e.preventDefault();
                    return;
                  }
                  if (graphyLogoOnClick) {
                    if (isOnlineCoursePlatform || isWebinarRebeccaSchocker) {
                      const scrollY = window.scrollY;
                      if (scrollY === 0) {
                        window.scrollTo({ top: 80, behavior: 'smooth' });
                      } else if (scrollY > 80) {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                      e.preventDefault();
                      return;
                    }
                    e.preventDefault();
                    window.open(graphyLogoOnClick.href, "_blank");
                    new Amplitude().sendEvent({
                      type: graphyLogoOnClick.amplitudeEvent?.eventName,
                      properties: {
                        ...graphyLogoOnClick.amplitudeEvent?.eventProperties,
                        current_url: getCurrentURL(),
                        utm_params: getFromLocal("utmFields"),
                        screen_width: getScreenWidth(),
                        referrer: getReferrer(),
                      } as { [key: string]: string | number },
                    });
                  }
                }}
              >
              <Image
                src="/images/graphy_logo_black.svg"
                width={100}
                height={32}
                alt="graphy"
                onClick={() => {
                  actionAmplitudeEvents("logo");
                }}
              />
            </Link>
          )}
          {promotionStripData ? (
            <div
              className={`${!showStrip ? "hidden" : ""} flex justify-center`}
            >
              <PromotionStrip data={promotionStripData} source="navbar" />
            </div>
          ) : (
            ""
          )}

          {promotionStripDataUS ? (
            <div
              className={`${!showStrip ? "hidden" : ""} flex justify-center`}
            >
              <PromotionStripUS data={promotionStripDataUS} source="navbar" />
            </div>
          ) : (
            ""
          )}
          <div className=" flex items-center gap-6">
            {navbarItems.map((item, index) => {
              if (item.type === NavItemType.Link) {
                return (
                  <Link
                    href={item.link as string}
                    key={"nav" + item.name + index}
                    className="text-caption font-normal text-grey-3 hover:text-sapphire02"
                    onClick={() => {
                      actionAmplitudeEvents(item.name);
                    }}
                    prefetch={false}
                  >
                    {item.name}
                  </Link>
                );
              } else {
                return (
                  <NavbarMenu
                    label={item.name}
                    key={"navbar-menu" + item.name + index}
                    menuItems={
                      item.menuItems as { name: string; link: string }[]
                    }
                    handleToggle={() => handleToggleAmplitude(item.name)}
                  />
                );
              }
            })}
            {signUpPage && renderCtaButton()}
          </div>
        </div>
      </nav>
      <div className="sticky top-0 z-bar md:hidden">
        <NavbarMweb
          signupCopies={signupCopies}
          navbarItems={navbarItems}
          showBookDemoBtn={showBookDemoBtn}
          hideNavCta={hideNavCta}
          graphyLogoOnClick={graphyLogoOnClick}
          registrationButton={registrationButton}
          registrationButtonUS={registrationButtonUS}
        />
      </div>
      {isAuthModalOpen && <SignInModal signupCopies={signupCopies} />}
    </>
  );
};
